import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { clientServicesAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, BookOpen, Calendar, ChevronRight, RefreshCw } from 'lucide-react';
import { formatPrice, formatDate } from '../lib/utils';

export default function Dashboard() {
  const [myServices, setMyServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyServices();
  }, []);

  const loadMyServices = async () => {
    try {
      const response = await clientServicesAPI.getMyServices();
      setMyServices(response.data);
    } catch (error) {
      console.error('Failed to load services', error);
    } finally {
      setLoading(false);
    }
  };

  const activeServices = myServices.filter(s => s.status === 'active');
  const upcomingRenewals = myServices
    .filter(s => s.renewal_date)
    .sort((a, b) => new Date(a.renewal_date) - new Date(b.renewal_date))
    .slice(0, 3);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Welcome Back!</h1>
            <p className="text-sm text-[#525252] mt-1">Here's an overview of your services and account</p>
          </div>
          <button 
            onClick={loadMyServices}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Active Services */}
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                <Package className="w-5 h-5 text-[#16A34A]" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{activeServices.length}</p>
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mt-1">Active Services</p>
          </div>

          {/* Total Services */}
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-[#2563EB]" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{myServices.length}</p>
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mt-1">Total Services</p>
          </div>

          {/* Browse Services */}
          <Link to="/services" className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md hover:border-[#D4D4D4] transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-[#9333EA]" />
              </div>
              <ChevronRight className="w-5 h-5 text-[#D4D4D4] group-hover:text-[#0A0A0A] transition-colors" />
            </div>
            <p className="text-sm font-semibold text-[#0A0A0A]">Browse Services</p>
            <p className="text-xs text-[#A3A3A3] mt-1">Explore all available</p>
          </Link>

          {/* Knowledge Base */}
          <Link to="/knowledge-base" className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md hover:border-[#D4D4D4] transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#D97706]" />
              </div>
              <ChevronRight className="w-5 h-5 text-[#D4D4D4] group-hover:text-[#0A0A0A] transition-colors" />
            </div>
            <p className="text-sm font-semibold text-[#0A0A0A]">Knowledge Base</p>
            <p className="text-xs text-[#A3A3A3] mt-1">Get help & guides</p>
          </Link>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* My Services List */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-[#EBEBEB]">
                <div>
                  <h3 className="text-base font-semibold text-[#0A0A0A]">My Active Services</h3>
                  <p className="text-sm text-[#525252]">{activeServices.length} services currently active</p>
                </div>
                <Link 
                  to="/my-services"
                  className="flex items-center gap-1 text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors"
                >
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-black rounded-full animate-spin mx-auto"></div>
                </div>
              ) : activeServices.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-14 h-14 rounded-xl bg-[#F5F5F5] flex items-center justify-center mx-auto mb-4">
                    <Package className="w-7 h-7 text-[#A3A3A3]" />
                  </div>
                  <h4 className="text-base font-semibold text-[#0A0A0A] mb-2">No Active Services</h4>
                  <p className="text-sm text-[#525252] mb-6">Browse our services to get started</p>
                  <Link 
                    to="/services"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-black text-white rounded-lg hover:bg-[#262626] transition-all"
                  >
                    Browse Services
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-[#EBEBEB]">
                  {activeServices.slice(0, 5).map((service) => (
                    <div key={service.id} className="flex items-center justify-between p-4 hover:bg-[#FAFAFA] transition-colors" data-testid={`service-card-${service.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#DCFCE7] flex items-center justify-center">
                          <Package className="w-5 h-5 text-[#16A34A]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#0A0A0A]">{service.service_name}</p>
                          <p className="text-xs text-[#A3A3A3]">{service.billing_cycle || 'One-time'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-[#0A0A0A]">{formatPrice(service.price || 0)}</p>
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                          service.status === 'active' ? 'bg-[#DCFCE7] text-[#16A34A]' : 
                          service.status === 'pending' ? 'bg-[#FEF3C7] text-[#D97706]' : 
                          'bg-[#F5F5F5] text-[#525252]'
                        }`}>
                          {service.status?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Upcoming Renewals */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-[#EBEBEB]">
                <h3 className="text-base font-semibold text-[#0A0A0A]">Upcoming Renewals</h3>
                <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-[#D97706]" />
                </div>
              </div>
              {upcomingRenewals.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-[#A3A3A3]">No upcoming renewals</p>
                </div>
              ) : (
                <div className="divide-y divide-[#EBEBEB]">
                  {upcomingRenewals.map((service, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 hover:bg-[#FAFAFA] transition-colors">
                      <div>
                        <p className="text-sm font-medium text-[#0A0A0A]">{service.service_name}</p>
                        <p className="text-xs text-[#A3A3A3]">{formatPrice(service.price)}</p>
                      </div>
                      <p className="text-xs font-medium text-[#D97706]">{formatDate(service.renewal_date)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-5">
              <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link 
                  to="/services"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
                    <ShoppingBag className="w-4 h-4 text-[#9333EA]" />
                  </div>
                  <span className="text-sm font-medium text-[#525252] group-hover:text-[#0A0A0A] flex-1">Browse Services</span>
                  <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />
                </Link>
                <Link 
                  to="/my-services"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                    <Package className="w-4 h-4 text-[#16A34A]" />
                  </div>
                  <span className="text-sm font-medium text-[#525252] group-hover:text-[#0A0A0A] flex-1">My Services</span>
                  <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />
                </Link>
                <Link 
                  to="/support"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F5F5F5] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-[#2563EB]" />
                  </div>
                  <span className="text-sm font-medium text-[#525252] group-hover:text-[#0A0A0A] flex-1">Get Support</span>
                  <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
